import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const PerformanceDashboard = ({ isOpen, onClose }) => {
  const [metrics, setMetrics] = useState(null);
  const [activeTab, setActiveTab] = useState('vitals');

  const refreshMetrics = useCallback(() => {
    if (window.performanceMonitor) {
      const report = window.performanceMonitor.getPerformanceReport();
      setMetrics(report);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      refreshMetrics();
      
      // Auto-refresh every 5 seconds
      const interval = setInterval(refreshMetrics, 5000);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [isOpen, refreshMetrics]);

  const getVitalColor = (rating) => {
    switch (rating) {
      case 'good': return 'text-green-600 bg-green-100';
      case 'needs-improvement': return 'text-yellow-600 bg-yellow-100';
      case 'poor': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Performance Dashboard</h2>
                <p className="text-blue-100 mt-1">Real-time performance monitoring</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${metrics?.isOnline ? 'bg-green-400' : 'bg-red-400'}`}></div>
                  <span className="text-sm">{metrics?.isOnline ? 'Online' : 'Offline'}</span>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'vitals', label: 'Core Web Vitals', icon: '⚡' },
                { id: 'session', label: 'Session Info', icon: '👤' },
                { id: 'resources', label: 'Resources', icon: '📦' },
                { id: 'errors', label: 'Errors', icon: '🚨' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {activeTab === 'vitals' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {metrics?.vitals && Object.entries(metrics.vitals).map(([key, vital]) => (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 uppercase">{key}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getVitalColor(vital.rating)}`}>
                          {vital.rating}
                        </span>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">
                        {key === 'cls' ? vital.value?.toFixed(3) : formatDuration(vital.value)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(vital.timestamp).toLocaleTimeString()}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {(!metrics?.vitals || Object.keys(metrics.vitals).length === 0) && (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-4">⏱️</div>
                    <p>Core Web Vitals are being measured...</p>
                    <p className="text-sm mt-1">Interact with the page to generate metrics</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'session' && metrics?.session && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Session Details</h3>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Session ID:</dt>
                        <dd className="text-sm font-mono text-gray-900">{metrics.session.id.slice(-8)}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Start Time:</dt>
                        <dd className="text-sm text-gray-900">
                          {new Date(Date.now() - metrics.session.startTime).toLocaleTimeString()}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Page Views:</dt>
                        <dd className="text-sm text-gray-900">{metrics.session.pageViews.length}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Interactions:</dt>
                        <dd className="text-sm text-gray-900">{metrics.session.interactions.length}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Errors:</dt>
                        <dd className="text-sm text-gray-900">{metrics.session.errors.length}</dd>
                      </div>
                    </dl>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Device Info</h3>
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Viewport:</dt>
                        <dd className="text-sm text-gray-900">
                          {metrics.session.viewport.width} × {metrics.session.viewport.height}
                        </dd>
                      </div>
                      {metrics.session.connection && (
                        <>
                          <div className="flex justify-between">
                            <dt className="text-sm text-gray-600">Connection:</dt>
                            <dd className="text-sm text-gray-900">{metrics.session.connection.effectiveType}</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm text-gray-600">Downlink:</dt>
                            <dd className="text-sm text-gray-900">{metrics.session.connection.downlink} Mbps</dd>
                          </div>
                          <div className="flex justify-between">
                            <dt className="text-sm text-gray-600">RTT:</dt>
                            <dd className="text-sm text-gray-900">{metrics.session.connection.rtt}ms</dd>
                          </div>
                        </>
                      )}
                    </dl>
                  </div>
                </div>

                {/* Recent Interactions */}
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Recent Interactions</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {metrics.session.interactions.slice(-10).reverse().map((interaction, index) => (
                      <div key={index} className="px-4 py-2 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {interaction.type}
                            </span>
                            <span className="text-sm text-gray-900">{interaction.element}</span>
                            {interaction.className && (
                              <span className="text-xs text-gray-500">.{interaction.className}</span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(interaction.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'resources' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Resource Performance</h3>
                {metrics?.metrics && Object.entries(metrics.metrics)
                  .filter(([key]) => key.startsWith('resource_'))
                  .slice(0, 20)
                  .map(([key, resource]) => (
                    <div key={key} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {resource.name.split('/').pop()}
                        </span>
                        <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                          {resource.type}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-gray-600">Duration</div>
                          <div className="font-medium">{formatDuration(resource.duration)}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Size</div>
                          <div className="font-medium">{formatBytes(resource.size)}</div>
                        </div>
                        <div>
                          <div className="text-gray-600">Start Time</div>
                          <div className="font-medium">{formatDuration(resource.startTime)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {activeTab === 'errors' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Error Log</h3>
                {metrics?.session?.errors?.length > 0 ? (
                  metrics.session.errors.map((error, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                              {error.type}
                            </span>
                            <span className="text-sm text-red-900 font-medium">
                              {error.message}
                            </span>
                          </div>
                          {error.filename && (
                            <div className="text-xs text-red-700">
                              {error.filename}:{error.line}:{error.column}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-red-600">
                          {new Date(error.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-4">✅</div>
                    <p>No errors detected</p>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={refreshMetrics}
                  className="bg-gray-600 text-white px-4 py-2 rounded text-sm hover:bg-gray-700 transition-colors"
                >
                  🔄 Refresh
                </button>
                <button
                  onClick={() => {
                    if (window.performanceMonitor) {
                      window.performanceMonitor.trackCustomEvent('dashboard-export', {
                        tab: activeTab,
                        metrics: Object.keys(metrics?.vitals || {}).length
                      });
                    }
                    const data = JSON.stringify(metrics, null, 2);
                    const blob = new Blob([data], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `performance-report-${Date.now()}.json`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 transition-colors"
                >
                  📄 Export
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PerformanceDashboard;
